/**
 * The backend scales font sizes with (size / 648) * target_height.
 * Preview font sizes must be scaled by (containerHeight / 648) to match.
 */
export const REFERENCE_PREVIEW_HEIGHT = 648;

export interface ApiTextOverlayResponse {
  id: string;
  text: string;
  font?: string;
  size?: number;
  color?: string;
  background_color?: string;
  background_style?: BackgroundStyle;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  strikethrough?: boolean;
  letter_case?: LetterCase;
  letter_spacing?: number;
  text_align?: string;
  border_color?: string;
  border_width?: number;
  border_radius?: number;
  border_style?: string;
  animation?: string;
  position_x_percentage?: number;
  position_y_percentage?: number;
  width_percentage?: number;
  start?: number; // seconds
  end?: number; // seconds
}

export interface ApiGifOverlayResponse {
  id: string;
  giphy_url: string;
  position_x_percentage: number;
  position_y_percentage: number;
  width_percentage: number;
  start?: number; // seconds
  end?: number; // seconds
}

export interface VideoResponse {
  id: string;
  title: string;
  url: string;
  created_at: string;
  duration_in_milliseconds: number;
  paragraphs: Paragraph[];
  subtitle_styles: SubtitleStyles;
  episode: Episode;
  text_overlays?: ApiTextOverlayResponse[];
  gif_overlays?: ApiGifOverlayResponse[];
  crop_positions?: Record<string, { x: number; y: number }>;
}

export interface Word {
  end: number;
  word: string;
  start: number;
  confidence: number;
}

export interface Paragraph {
  id: string;
  start: number;
  end: number;
  text: string;
  words: SentenceObject[];
}

export interface SubtitleStyles {
  font?: string;
  size?: number;
  color?: string;
  background_style?: BackgroundStyle;
  background_color?: string;
  outline_thickness?: number;
  outline_color?: string;
  text_wrap?: boolean;
  line_height?: number;
  letter_case?: LetterCase;
  letter_spacing?: number;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  strikethrough?: boolean;
  border_color?: string;
  border_width?: number;
  border_radius?: number;
  border_style?: string;
  margin_top?: number;
  margin_left?: number;
  text_align?: string;
  text_handle_position_x_percentage?: number;
  text_handle_position_y_percentage?: number;
  text_handle_width_percentage?: number;
}

export interface Episode {
  id: string;
  name: string;
  project: Project;
}

export interface Project {
  id: string;
  name: string;
}

export interface SentenceObject {
  start: number | null;
  end: number | null;
  word: string;
}

export enum LetterCase {
  DEFAULT = "default",
  UPPERCASE = "uppercase",
  LOWERCASE = "lowercase",
  CAPITALIZE = "capitalize",
}

export enum BackgroundStyle {
  NONE = "none",
  SOLID = "solid",
  OUTLINE = "outline",
  FULL = "full",
  ROUNDED = "rounded",
}

export enum ExportVideoCompressionLevel {
  HIGHER_QUALITY = "higher_quality",
  BALANCED = "balanced",
  VERY_FAST = "very_fast",
}

export type TextAnimation =
  | "none"
  | "fade-in"
  | "fade-out"
  | "slide-up"
  | "slide-down"
  | "slide-left"
  | "slide-right"
  | "zoom-in"
  | "zoom-out"
  | "pop"
  | "typewriter"
  | "bounce"
  | "pulse"
  | "rotate";

export interface TextOverlay {
  id: string;
  text: string;
  x: number; // percentage 0-100
  y: number; // percentage 0-100
  width: number; // percentage 0-100
  height: number; // percentage 0-100
  startMs: number;
  endMs: number;
  fontSize: number;
  fontFamily: string;
  color: string;
  backgroundColor: string;
  backgroundStyle?: BackgroundStyle;
  bold: boolean;
  italic: boolean;
  underline: boolean;
  strikethrough: boolean;
  letterCase?: LetterCase;
  letterSpacing?: number;
  textAlign?: string;
  borderColor?: string;
  borderWidth?: number;
  borderRadius?: number;
  borderStyle?: string;
  animation?: TextAnimation;
}

export interface GifOverlay {
  id: string;
  giphyId: string;
  title: string; // Giphy GIF title
  url: string; // GIF URL (small/fixed size for preview)
  originalUrl: string; // Original quality for export
  x: number; // percentage-based X position
  y: number; // percentage-based Y position
  width: number; // percentage-based width
  height: number; // percentage-based height
  startMs: number; // start time in milliseconds
  endMs: number; // end time in milliseconds
}

export enum ExportVideoResolution {
  "320p" = "320p",
  "480p" = "480p",
  "720p" = "720p",
  "1080p" = "1080p",
  "4k" = "4k",
  "instagram" = "instagram",
  "tiktok" = "tiktok",
  "youtube-shorts" = "youtube-shorts",
}
